import { DollarSign, TrendingUp, Shield, Users } from 'lucide-react';

export default function FinancialPlanning() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <DollarSign className="w-12 h-12 text-yellow-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Financial Planning Track
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            For qualified clients who become customers, we waive all software fees and provide
            comprehensive financial planning services.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <TrendingUp className="w-8 h-8 text-yellow-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Wealth Building</h3>
            <p className="text-gray-600">
              Strategic financial planning to help you build and preserve wealth throughout your career
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Risk Management</h3>
            <p className="text-gray-600">
              Protect what matters most with comprehensive insurance and risk mitigation strategies
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Family Legacy</h3>
            <p className="text-gray-600">
              Estate planning and strategies to create lasting wealth for future generations
            </p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-12 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            How It Works
          </h2>
          <div className="space-y-6 max-w-3xl mx-auto">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                1
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Use Our Software</h4>
                <p className="text-gray-600">
                  Subscribe to CareerIQ and leverage our AI-powered resume optimization,
                  LinkedIn profile building, and content generation tools.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                2
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Meet Qualification Criteria</h4>
                <p className="text-gray-600">
                  Our financial planning track is available to qualified professionals who meet
                  specific income and asset requirements.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                3
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Software Fees Waived</h4>
                <p className="text-gray-600">
                  When you become a financial planning client, we waive all CareerIQ software fees.
                  Continue using all features at no cost.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                4
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Comprehensive Planning</h4>
                <p className="text-gray-600">
                  Receive personalized financial planning services including investment management,
                  tax planning, retirement planning, and estate planning.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-2xl shadow-xl p-12 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Interested in Learning More?</h2>
          <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
            Schedule a consultation to see if the Financial Planning Track is right for you.
            There's no obligation and we'll help you understand your options.
          </p>
          <a
            href="mailto:planning@careeriq.com"
            className="inline-flex items-center px-8 py-4 bg-white text-yellow-700 rounded-lg font-bold text-lg hover:bg-gray-50 transition shadow-lg"
          >
            Schedule a Consultation
          </a>
        </div>

        <div className="mt-16 bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
            What Our Financial Planning Clients Say
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border-l-4 border-yellow-600 pl-4">
              <p className="text-gray-700 mb-2">
                "The combination of career services and financial planning has been transformative.
                Not only did I land my dream job, but now I have a clear path to financial independence."
              </p>
              <p className="text-sm text-gray-600 font-medium">- Sarah M., VP of Product</p>
            </div>
            <div className="border-l-4 border-yellow-600 pl-4">
              <p className="text-gray-700 mb-2">
                "Having my software fees waived while getting comprehensive financial planning is
                incredible value. The team understands the unique challenges executives face."
              </p>
              <p className="text-sm text-gray-600 font-medium">- Michael T., CTO</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
